import { useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { MultilingualInput } from './ui/multilingual-input'
import { useToast } from '../hooks/use-toast'
import { useCreatePropertyDefinition, usePropertyDefinitions } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import type { CreatePropertyDefinitionRequest } from '../hooks/useQueries'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { compactLocalized } from '../lib/localization'

const DATA_TYPE_VALUES = ['text', 'number', 'boolean', 'date', 'enum', 'json'] as const
const ENTITY_TYPE_VALUES = ['asset', 'document', 'user'] as const

export function PropertyCreatePage() {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation('properties')
  const [formData, setFormData] = useState({
    name: { en: '', fr: '' },
    description: { en: '', fr: '' } as Record<string, string>,
    code: '',
    data_type: '' as CreatePropertyDefinitionRequest['data_type'] | '',
    target_entity_types: [] as string[],
    enabled: true,
    with_time: false,
    regex_pattern: '',
    min_value: '',
    max_value: '',
    integer_only: false,
    system_managed: false,
    hidden: false,
    suggestions_enabled: false,
    suggestions_scope: 'asset_type',
    suggestions_depends_on: [] as string[],
  })
  const [isCodeManuallyEdited, setIsCodeManuallyEdited] = useState(false)

  const createProperty = useCreatePropertyDefinition(org || 'demo')
  const { data: allProperties = [] } = usePropertyDefinitions(org || 'demo')

  // Candidates for `suggestions.depends_on`: the organization's enabled
  // text/enum asset properties.
  const dependencyCandidates = useMemo(() => allProperties
    .filter(p => (p.data_type === 'text' || p.data_type === 'enum')
      && (p.target_entity_types || []).includes('asset')
      && p.enabled !== false)
    .map(p => ({
      code: p.code || '',
      label: (typeof p.name === 'object' && p.name !== null
        ? (p.name as Record<string, string>).en || (p.name as Record<string, string>).fr || p.code
        : p.code) || '',
    }))
    .filter(p => p.code)
    .sort((a, b) => a.label.localeCompare(b.label)),
    [allProperties])

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('mapping_rules.breadcrumb_home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/properties` },
    { title: t('create.title') }
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  // Auto-generate code from name
  const handleNameChange = useCallback((value: { [key: string]: string }) => {
    setFormData(prev => {
      const updates: typeof prev = { ...prev, name: { en: value.en || '', fr: value.fr || '' } }

      // Auto-generate code from English name only if it hasn't been manually edited
      if (!isCodeManuallyEdited && value.en) {
        updates.code = value.en
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          .substring(0, 50) // Limit length
      }

      return updates
    })
  }, [isCodeManuallyEdited])

  const handleCodeChange = useCallback((value: string) => {
    setIsCodeManuallyEdited(true)
    setFormData(prev => ({ ...prev, code: value }))
  }, [])

  const handleEntityTypeToggle = useCallback((entityType: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      target_entity_types: checked
        ? [...prev.target_entity_types, entityType]
        : prev.target_entity_types.filter(type => type !== entityType)
    }))
  }, [])

  const isFormValid = useMemo(() => {
    // At least one language in name must have at least 2 characters
    const hasValidName = Object.values(formData.name).some(name => name.trim().length >= 2)

    return (
      hasValidName &&
      formData.code.trim().length > 0 &&
      formData.data_type !== '' &&
      formData.target_entity_types.length > 0
    )
  }, [formData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isFormValid) {
      toast({
        title: t('common:status.error'),
        description: t('create.validation_error'),
        variant: "destructive",
      })
      return
    }

    try {
      const propertyData: CreatePropertyDefinitionRequest = {
        enabled: formData.enabled,
        name: formData.name,
        code: formData.code.trim(),
        data_type: formData.data_type as CreatePropertyDefinitionRequest['data_type'],
        target_entity_types: formData.target_entity_types as CreatePropertyDefinitionRequest['target_entity_types'],
      }

      const description = compactLocalized(formData.description)
      if (Object.keys(description).length > 0) {
        propertyData.description = description
      }

      // Include properties field based on data type
      const properties: Record<string, any> = {}
      
      if (formData.data_type === 'date') {
        properties.with_time = formData.with_time
      }
      
      if (formData.data_type === 'text' && formData.regex_pattern.trim()) {
        properties.regex_pattern = formData.regex_pattern.trim()
      }

      // Value suggestions (spec 2026-09-02-03). Absent key = no suggestions.
      if (formData.data_type === 'text' && formData.suggestions_enabled) {
        const suggestions: Record<string, unknown> = { scope: formData.suggestions_scope }
        if (formData.suggestions_depends_on.length > 0) {
          suggestions.depends_on = formData.suggestions_depends_on
        }
        properties.suggestions = suggestions
      }
      
      if (formData.data_type === 'number') {
        if (formData.min_value.trim()) {
          const minVal = parseFloat(formData.min_value)
          if (!isNaN(minVal)) {
            properties.min_value = minVal
          }
        }
        
        if (formData.max_value.trim()) {
          const maxVal = parseFloat(formData.max_value)
          if (!isNaN(maxVal)) {
            properties.max_value = maxVal
          }
        }
        
        if (formData.integer_only) {
          properties.integer_only = true
        }
      }
      
      if (Object.keys(properties).length > 0) {
        propertyData.properties = properties
      }

      // Build flags array
      const flags: string[] = []
      if (formData.system_managed) {
        flags.push('system_managed')
      }
      if (formData.hidden) {
        flags.push('hidden')
      }
      if (flags.length > 0) {
        ;(propertyData as any).flags = flags
      }

      await createProperty.mutateAsync(propertyData)

      navigate({ to: `/${org}/properties` })
    } catch (error) {
      console.error('Error creating property:', error)
      toast({
        title: t('common:status.error'),
        description: t('create.create_error'),
        variant: "destructive",
      })
    }
  }

  const handleCancel = () => {
    navigate({ to: `/${org}/properties` })
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.properties}
            title={t('create.title')}
            description={t('create.subtitle')}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name Field */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t('create.name_label')} <span className="text-destructive">*</span>
            </Label>
            <MultilingualInput
              value={formData.name}
              onChange={handleNameChange}
              placeholder={t('create.name_label')}
              required
              data-testid="property-name"
            />
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <Label htmlFor="description-en" className="text-sm font-medium">
              {t('create.description_label')}
            </Label>
            <MultilingualInput
              id="description"
              value={formData.description}
              onChange={(description) => setFormData(prev => ({ ...prev, description }))}
              placeholder={t('create.description_placeholder')}
              data-testid="property-description"
            />
            {Object.values(formData.description).some(text => text.length > 500) && (
              <p className="text-sm text-destructive">{t('create.description_too_long')}</p>
            )}
          </div>

          {/* Code Field */}
          <div className="space-y-2">
            <Label htmlFor="code" className="text-sm font-medium">
              {t('create.code_label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder={t('create.code_placeholder')}
              className="w-full font-mono"
              data-testid="property-code"
            />
            <p className="text-xs text-muted-foreground">
              {t('create.code_hint')}
            </p>
          </div>

          {/* Data Type Field */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t('create.data_type_label')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.data_type}
              onValueChange={(value) => setFormData(prev => ({
                ...prev,
                data_type: value as CreatePropertyDefinitionRequest['data_type']
              }))}
            >
              <SelectTrigger className="w-full" data-testid="property-data-type">
                <SelectValue placeholder={t('create.data_type_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {DATA_TYPE_VALUES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`data_types.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target Entity Types */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t('create.target_entity_types_label')} <span className="text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              {t('create.target_entity_types_hint')}
            </p>
            <div className="space-y-2">
              {ENTITY_TYPE_VALUES.map((value) => (
                <div key={value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`entity-${value}`}
                    checked={formData.target_entity_types.includes(value)}
                    onCheckedChange={(checked) =>
                      handleEntityTypeToggle(value, checked as boolean)
                    }
                    data-testid={`entity-type-${value}`}
                  />
                  <Label htmlFor={`entity-${value}`} className="text-sm">
                    {t(`entity_types.${value}`)}
                  </Label>
                </div>
              ))}
            </div>
            {formData.target_entity_types.length === 0 && (
              <p className="text-sm text-destructive">{t('create.target_entity_types_required')}</p>
            )}
          </div>

          {/* Date Type Properties */}
          {formData.data_type === 'date' && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="with-time"
                  checked={formData.with_time}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, with_time: checked as boolean }))
                  }
                  data-testid="with-time-checkbox"
                />
                <Label htmlFor="with-time" className="text-sm">
                  {t('create.with_time_label')}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('create.with_time_hint')}
              </p>
            </div>
          )}

          {/* Text Type Properties */}
          {formData.data_type === 'text' && (
            <div className="space-y-2">
              <Label htmlFor="regex-pattern" className="text-sm font-medium">
                {t('create.regex_label')}
              </Label>
              <Input
                id="regex-pattern"
                value={formData.regex_pattern}
                onChange={(e) => setFormData(prev => ({ ...prev, regex_pattern: e.target.value }))}
                placeholder={t('create.regex_placeholder')}
                className="w-full font-mono"
                data-testid="regex-pattern-input"
              />
              <p className="text-xs text-muted-foreground">
                {t('create.regex_hint')}
              </p>
            </div>
          )}

          {/* Value suggestions - text only (spec 2026-09-02-03) */}
          {formData.data_type === 'text' && (
            <div className="space-y-4 rounded-lg border border-border p-4" data-testid="suggestions-card">
              <h4 className="text-sm font-medium">{t('settings.suggestions.title')}</h4>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="suggestions-enabled"
                  checked={formData.suggestions_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, suggestions_enabled: checked === true }))}
                  data-testid="suggestions-enabled-checkbox"
                />
                <Label htmlFor="suggestions-enabled" className="text-sm font-normal">
                  {t('settings.suggestions.enable')}
                </Label>
              </div>
              {formData.suggestions_enabled && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('settings.suggestions.scope')}</Label>
                  <Select
                    value={formData.suggestions_scope}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, suggestions_scope: value }))}
                  >
                    <SelectTrigger data-testid="suggestions-scope-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asset_type">{t('settings.suggestions.scope_asset_type')}</SelectItem>
                      <SelectItem value="organization">{t('settings.suggestions.scope_organization')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Label className="text-sm font-medium">{t('settings.suggestions.depends_on')}</Label>
                  <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-border p-3">
                    {dependencyCandidates.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('settings.suggestions.none')}</p>
                    ) : dependencyCandidates.map(candidate => (
                      <label key={candidate.code} className="flex items-center space-x-2 text-sm">
                        <Checkbox
                          checked={formData.suggestions_depends_on.includes(candidate.code)}
                          onCheckedChange={(checked) => setFormData(prev => ({
                            ...prev,
                            suggestions_depends_on: checked === true
                              ? [...prev.suggestions_depends_on, candidate.code]
                              : prev.suggestions_depends_on.filter(c => c !== candidate.code),
                          }))}
                          data-testid={`suggestions-depends-on-${candidate.code}`}
                        />
                        <span>{candidate.label}</span>
                        <code className="text-xs text-muted-foreground">{candidate.code}</code>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('settings.suggestions.depends_on_help')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Number Type Properties */}
          {formData.data_type === 'number' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min-value" className="text-sm font-medium">
                    {t('create.min_value_label')}
                  </Label>
                  <Input
                    id="min-value"
                    type="number"
                    step="any"
                    value={formData.min_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_value: e.target.value }))}
                    placeholder={t('create.min_value_placeholder')}
                    className="w-full"
                    data-testid="min-value-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('create.min_value_hint')}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-value" className="text-sm font-medium">
                    {t('create.max_value_label')}
                  </Label>
                  <Input
                    id="max-value"
                    type="number"
                    step="any"
                    value={formData.max_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_value: e.target.value }))}
                    placeholder={t('create.max_value_placeholder')}
                    className="w-full"
                    data-testid="max-value-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('create.max_value_hint')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="integer-only"
                  checked={formData.integer_only}
                  onCheckedChange={(checked) =>
                    setFormData(prev => ({ ...prev, integer_only: checked as boolean }))
                  }
                  data-testid="integer-only-checkbox"
                />
                <Label htmlFor="integer-only" className="text-sm">
                  {t('create.integer_only_label')}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('create.integer_only_hint')}
              </p>
            </div>
          )}

          {/* Enabled Status */}
          <div className="space-y-3 border-t border-border pt-6">
            <Label className="text-sm font-medium">{t('create.status_label')}</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) =>
                  setFormData(prev => ({ ...prev, enabled: checked as boolean }))
                }
                data-testid="enabled-checkbox"
              />
              <Label htmlFor="enabled" className="text-sm">
                {t('create.enabled_label')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('create.enabled_hint')}
            </p>
            <div className="flex items-center space-x-2 mt-3">
              <Checkbox
                id="system-managed"
                checked={formData.system_managed}
                onCheckedChange={(checked) =>
                  setFormData(prev => ({ ...prev, system_managed: checked as boolean }))
                }
                data-testid="system-managed-checkbox"
              />
              <Label htmlFor="system-managed" className="text-sm">
                {t('create.system_managed_label')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('create.system_managed_hint')}
            </p>
            <div className="flex items-center space-x-2 mt-3">
              <Checkbox
                id="hidden"
                checked={formData.hidden}
                onCheckedChange={(checked) =>
                  setFormData(prev => ({ ...prev, hidden: checked as boolean }))
                }
                data-testid="hidden-flag-checkbox"
              />
              <Label htmlFor="hidden" className="text-sm">
                {t('create.hidden_label')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('create.hidden_hint')}
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              data-testid="cancel-button"
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || createProperty.isPending}
              data-testid="create-button"
            >
              {createProperty.isPending ? t('create.creating') : t('create.create_button')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}